import {
  AvatarGroup,
  type AvatarGroupProps,
  forwardRef,
} from "@chakra-ui/react";
import { Avatar } from "@components/ui-kit/avatar";
import { AvatarPlaceholder } from "@components/ui-kit/avatar/variants/placeholder";
import type {
  AvatarStackDensity,
  AvatarStackProps,
  OverflowAvatarProps,
  StackAvatarProps,
} from "@components/ui-kit/avatar/variants/stack/types";
import { t } from "@lingui/macro";
import type { ReactElement, ReactNode } from "react";

const DEFAULT_MAX = 3;
// gray.100, a hex because the text color is computed from it
const OVERFLOW_COLOR = "#EDF2F7";

// Negative spacing is the avatar overlap; undefined keeps Chakra's default
const AVATAR_SPACING: Record<AvatarStackDensity, string | undefined> = {
  compact: undefined,
  comfortable: "-1",
};

/**
 * Overlapping avatars, a "+N" avatar for those over `max`, and an optional
 * dashed "add" avatar.
 */
export const AvatarStack = forwardRef<AvatarStackProps, "div">(
  function AvatarStack(
    {
      items,
      max = DEFAULT_MAX,
      density = "compact",
      isDisabled = false,
      isTooltipDisabled,
      onItemClick,
      addLabel,
      onAddClick,
      renderAvatar,
      ...props
    },
    ref,
  ) {
    const spacing = AVATAR_SPACING[density];
    const hiddenItems = items.slice(max);

    return (
      <AvatarGroup ref={ref} spacing={spacing} {...props}>
        {/* An empty group would still take the overlap and pull "+" left */}
        {items.length > 0 && (
          <AvatarGroupInner spacing={spacing}>
            {items.slice(0, max).map((item) => (
              <StackAvatar
                key={`avatar-stack-${item.id}`}
                item={item}
                isDisabled={isDisabled}
                isTooltipDisabled={isTooltipDisabled}
                onClick={onItemClick ? () => onItemClick(item) : undefined}
                renderAvatar={renderAvatar}
              />
            ))}
            {hiddenItems.length > 0 && (
              <OverflowAvatar
                items={hiddenItems}
                isTooltipDisabled={isTooltipDisabled}
              />
            )}
          </AvatarGroupInner>
        )}
        {onAddClick && (
          <AvatarPlaceholder
            label={addLabel ?? t`Add`}
            isDisabled={isDisabled}
            isTooltipDisabled={isTooltipDisabled}
            onClick={() => onAddClick()}
          />
        )}
      </AvatarGroup>
    );
  },
);

/**
 * The group styles (overlap, border) its direct children, so they land
 * on the avatar here, never on a wrapper from `renderAvatar`.
 */
function StackAvatar({
  item,
  renderAvatar,
  ...props
}: StackAvatarProps): ReactNode {
  const avatar: ReactElement = (
    <Avatar
      {...props}
      name={item.name}
      src={item.src}
      colorScheme={item.colorScheme}
      tooltip={item.tooltip === false ? undefined : (item.tooltip ?? item.name)}
    />
  );

  return renderAvatar ? renderAvatar(avatar, item) : avatar;
}

/** "+N" with the hidden names on hover; a click falls through to the parent. */
function OverflowAvatar({ items, ...props }: OverflowAvatarProps): ReactNode {
  return (
    <Avatar
      {...props}
      name={`+${items.length}`}
      getInitials={(name) => name}
      colorScheme={OVERFLOW_COLOR}
      tooltip={items.map(({ name }) => name).join(", ")}
    />
  );
}

/**
 * Keeps `showBorder` from the outer AvatarGroup off the DOM.
 * @see https://github.com/chakra-ui/chakra-ui/issues/8808
 */
function AvatarGroupInner({
  showBorder: _showBorder,
  ...props
}: AvatarGroupProps & { showBorder?: boolean }) {
  return <AvatarGroup {...props} />;
}
